/* 소설 리더의 문단 — 대화 메시지(segment)에서 바꿔 쓰는 최소 단위. 대화방 · 엔딩 소설 · 라운지 회차가 같은 모양을 넘긴다.
   대사 = 따옴표 “ ” 로 감싸 그리고, 지문 = 그대로, 내 입력 = 별도 라벨 없이 같은 본문 문단, 장면 컷 = 문단 사이 그림,
   slot = 명령어 결과 카드처럼 리더가 모양을 모르는 것(넘긴 노드를 문단 사이에 그대로 둔다). */
import type { ReactNode } from 'react'

import type { ChatItem } from '@/features/veluvit/lib/chat'

export type ReaderBlock =
  | { kind: 'speech'; key: string; text: string; speaker?: string }
  | { kind: 'narration'; key: string; text: string }
  | { kind: 'user'; key: string; text: string; aiPlayer?: boolean }
  | { kind: 'scene'; key: string; src: string; fallback?: string }
  | { kind: 'slot'; key: string; node: ReactNode }

/** 대화방 한 줄 → 문단. 리더가 그리지 않는 줄(상태 · 명령어 · 안내)은 null — 대화방이 원래 모양 그대로 끼운다 */
export function blockOfItem(
  item: ChatItem,
  image: (kind: string) => string,
  fallback?: string
): ReaderBlock | null {
  switch (item.t) {
    case 'char':
      return { kind: 'speech', key: item.key, text: item.v, speaker: item.sp }
    case 'sit':
      return { kind: 'narration', key: item.key, text: item.v }
    case 'user':
      return {
        kind: 'user',
        key: item.key,
        text: item.v,
        aiPlayer: item.aiPlayer,
      }
    case 'image':
      return { kind: 'scene', key: item.key, src: item.src }
    case 'kindImage':
      return { kind: 'scene', key: item.key, src: image(item.kind), fallback }
    default:
      return null
  }
}

/** 이용자 자리 표시({{user}} · {user} · {유저})를 이름으로 바꾼다 */
export function fillUser(text: string, userName?: string): string {
  if (!userName) return text
  return text.replace(
    /\{\{\s*user\s*\}\}|\{\s*user\s*\}|\{\s*유저\s*\}/gi,
    userName
  )
}

const WRAP = /^(["“”「『'‘]+)([\s\S]*?)(["“”」』'’]+)$/
const DOUBLE = /["“”「『」』]/

/**
 * 대사 안쪽 글 — 원문이 양끝을 따옴표로 감싸고 있을 때만 떼어 낸다(겹친 "“…"” 도 한 번에). 리더 · 말풍선이 자기 식으로 다시 감싼다.
 * 한쪽만 있거나 안에 또 큰따옴표가 있으면(“그래” 하고 “응”) 원문 그대로 — 예전엔 양끝을 따로 떼어 그런 글이 잘렸다(2026-10-13)
 */
export function speechInner(text: string): string {
  const source = text.trim()
  const match = WRAP.exec(source)
  if (!match) return source
  const inner = match[2].trim()
  return DOUBLE.test(inner) ? source : inner
}

/** 형광펜 조각 — 발췌 글을 문단 · 줄 단위로 쪼개고 따옴표를 뗀다(문단 글과 비교하려고) */
export function highlightFragments(texts: string[]): string[] {
  const out = new Set<string>()
  for (const text of texts) {
    for (const line of text.split(/\n+/)) {
      const piece = speechInner(line)
      if (piece.length >= 2) out.add(piece)
    }
  }
  return [...out]
}

/** 글에서 형광펜 조각이 걸리는 구간 — 겹치는 구간은 합친다 */
export function highlightRanges(
  text: string,
  fragments: string[]
): [number, number][] {
  const ranges: [number, number][] = []
  for (const piece of fragments) {
    let at = text.indexOf(piece)
    while (at >= 0) {
      ranges.push([at, at + piece.length])
      at = text.indexOf(piece, at + piece.length)
    }
  }
  ranges.sort((a, b) => a[0] - b[0])
  const merged: [number, number][] = []
  for (const r of ranges) {
    const last = merged.at(-1)
    if (last && r[0] <= last[1]) last[1] = Math.max(last[1], r[1])
    else merged.push([r[0], r[1]])
  }
  return merged
}
